import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useLocation } from "wouter";
import { useState } from "react";
import {
  Loader2,
  Users,
  Tv,
  Search,
  ChevronLeft,
  Clock,
  Eye,
  Shield,
  Trophy,
  Radio,
  Activity,
  Sparkles,
  Gamepad2,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { type GameRoom } from "@shared/schema";
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { MobileHeader } from "@/components/ui/mobile-header";
import PullToRefresh from "react-simple-pull-to-refresh";
import { EnhancedHeader } from "@/components/ui/enhanced-header";

const GameModeIcons = {
  public: <Radio className="h-4 w-4" />,
  friends: <Users className="h-4 w-4" />,
  tournament: <Trophy className="h-4 w-4" />,
  ranked: <Shield className="h-4 w-4" />,
};

const GameStatusIndicator = ({
  status,
}: {
  status: "waiting" | "playing" | "full";
}) => {
  const colors = {
    waiting: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    playing: "bg-green-500/20 text-green-400 border-green-500/30",
    full: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  const labels = {
    waiting: "Waiting",
    playing: "In Progress",
    full: "Full",
  };

  return (
    <Badge variant="outline" className={cn("text-xs py-1", colors[status])}>
      <span
        className={`mr-1.5 h-1.5 w-1.5 rounded-full inline-block ${status === "playing" ? "animate-pulse" : ""} ${colors[status].split(" ")[1]}`}
      ></span>
      {labels[status]}
    </Badge>
  );
};

export default function WatchPage() {
  const { user } = useAuth();
  const [_, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("public");
  const [view, setView] = useState<"grid" | "list">("grid");
  const queryClient = useQueryClient();

  const { data: gameRooms, isLoading } = useQuery({
    queryKey: ["watchable-games", filter],
    queryFn: async () => {
      const endpoint =
        filter === "friends"
          ? "/api/watch/friends"
          : filter === "tournament"
            ? "/api/watch/tournaments"
            : filter === "ranked"
              ? "/api/watch/ranked"
              : "/api/watch/public";
      const res = await apiRequest("GET", endpoint);
      if (!res.ok) throw new Error("Failed to fetch games");
      return res.json();
    },
  });

  const handleWatchGame = async (id: string) => {
    if (!id.trim()) return;
    setLocation(`/game/${id}`);
  };

  if (!user) {
    setLocation("/auth");
    return null;
  }

  const filterGames = (rooms: any[] = []) => {
    return rooms.filter((room) => {
      if (filter === "tournament" && room.spectatorMode !== "tournament")
        return false;
      if (filter === "friends" && room.spectatorMode !== "friends")
        return false;
      if (filter === "public" && room.spectatorMode !== "public") return false;
      if (filter === "ranked" && room.spectatorMode !== "ranked") return false;

      return (
        room.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        room.player1Info?.username
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        room.player2Info?.username
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    });
  };

  const getGameStatus = (room: any) => {
    const playerCount = (room.player1 ? 1 : 0) + (room.player2 ? 1 : 0);
    if (playerCount < 2) return "waiting";
    return "playing"; // we're assuming game is in progress if 2 players are there
  };

  const filteredGames = filterGames(gameRooms);
  const hasGames = filteredGames && filteredGames.length > 0;

  const handleRefresh = async () => {
    // Invalidate the current filter's games
    await queryClient.invalidateQueries({ queryKey: ["watchable-games", filter] });
    return Promise.resolve();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-muted-foreground animate-pulse">
            Loading available games...
          </p>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-b from-black via-black/95 to-black/90 text-white">
        {/* Enhanced header */}
       <EnhancedHeader
          title="Live Games"
          backPath="/"
          coloredText={true}
          rightContent={null}
          showBackButton={false}
        />
    <PullToRefresh 
        onRefresh={handleRefresh}
        pullingContent={
          <div className="text-center p-2">
            <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
            <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
          </div>
        }
        refreshingContent={
          <div className="text-center p-4">
            <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
            <p className="text-sm text-white/60 mt-1">Refreshing games...</p>
          </div>
        }
      >

        <div>

      {/* Control Bar */}
        <div className="sticky z-10 bg-black/60 backdrop-blur-md border-b border-white/5 px-4">
          <div className="container max-w-6xl mx-auto">
            <div className="flex items-center w-full">
              <div className="relative flex-1 w-full flex items-center">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  placeholder="Search by game name or player..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 py-5 bg-black/40 border-white/10 focus:border-primary/50 rounded-lg w-full"
                />

                <div className="flex gap-2 ml-3">
                  <Button
                    variant={view === "grid" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setView("grid")}
                    className={cn(
                      view === "grid"
                        ? "bg-primary text-black hover:bg-primary/90"
                        : "border-white/10 hover:bg-white/5 text-white/70",
                    )}
                  >
                    Grid
                  </Button>
                  <Button
                    variant={view === "list" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setView("list")}
                    className={cn(
                      view === "list"
                        ? "bg-primary text-black hover:bg-primary/90"
                        : "border-white/10 hover:bg-white/5 text-white/70",
                    )}
                  >
                    List
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Game Categories */}
        <div className="container max-w-6xl mx-auto px-4 mt-4">
          <Tabs
            defaultValue={filter}
            onValueChange={setFilter}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-4 bg-black/40 border border-white/10 mb-4 rounded-lg p-1">
              <TabsTrigger
                value="public"
                className={cn(
                  "rounded-md transition-all duration-300",
                  filter === "public"
                    ? "bg-primary/90 text-black data-[state=active]:bg-primary"
                    : "hover:bg-white/5",
                )}
              >
                <Radio className="h-4 w-4 mr-2" />
                Public
              </TabsTrigger>
              <TabsTrigger
                value="friends"
                className={cn(
                  "rounded-md transition-all duration-300",
                  filter === "friends"
                    ? "bg-primary/90 text-black data-[state=active]:bg-primary"
                    : "hover:bg-white/5",
                )}
              >
                <Users className="h-4 w-4 mr-2" />
                Friends
              </TabsTrigger>
              <TabsTrigger
                value="tournament"
                className={cn(
                  "rounded-md transition-all duration-300",
                  filter === "tournament"
                    ? "bg-primary/90 text-black data-[state=active]:bg-primary"
                    : "hover:bg-white/5",
                )}
              >
                <Trophy className="h-4 w-4 mr-2" />
                Tournament
              </TabsTrigger>
              <TabsTrigger
                value="ranked"
                className={cn(
                  "rounded-md transition-all duration-300",
                  filter === "ranked"
                    ? "bg-primary/90 text-black data-[state=active]:bg-primary"
                    : "hover:bg-white/5",
                )}
              >
                <Shield className="h-4 w-4 mr-2" />
                Ranked
              </TabsTrigger>
            </TabsList>

            {/* Content for all tabs */}
            <AnimatePresence mode="wait">
              <motion.div
                key={filter}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* No games message */}
                {!hasGames && (
                  <div className="h-60 flex flex-col items-center justify-center text-center p-8 rounded-lg bg-black/40 border border-white/10">
                    <Gamepad2 className="h-12 w-12 text-white/20 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">
                      No {filter} games available
                    </h3>
                    <p className="text-white/60 max-w-md">
                      There are currently no {filter} games to watch. Check back
                      later or try another category.
                    </p>
                  </div>
                )}

                {/* Games grid/list */}
                <div
                  className={cn(
                    view === "grid"
                      ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                      : "flex flex-col gap-3",
                  )}
                >
                  {filteredGames.map((room: any) =>
                    view === "grid" ? (
                      <GameCardGridView
                        key={room.id}
                        room={room}
                        gameStatus={getGameStatus(room)}
                        handleWatchGame={handleWatchGame}
                      />
                    ) : (
                      <GameCardListView
                        key={room.id}
                        room={room}
                        gameStatus={getGameStatus(room)}
                        handleWatchGame={handleWatchGame}
                      />
                    ),
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </Tabs>
        </div>
        </div>

        </PullToRefresh>

      </div>
  );
}

function GameCardGridView({
  room,
  gameStatus,
  handleWatchGame,
}: {
  room: any;
  gameStatus: "waiting" | "playing" | "full";
  handleWatchGame: (id: string) => void;
}) {
  return (
    <motion.div
      whileHover={{ y: -5, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
      <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden hover:border-primary/60 transition-all">
        <div className="relative bg-gradient-to-br from-black/80 to-black/40 p-4">
          <div className="flex justify-between items-start">
            <h3 className="font-bold text-lg text-white">{room.name}</h3>
            <div className="flex gap-2">
              <GameStatusIndicator status={gameStatus} />
            </div>
          </div>

          <div className="absolute top-3 right-3">
            <Badge variant="outline" className="bg-black/60 border-white/20">
              <Eye className="h-3 w-3 mr-1" />
              {room.currentViewers || 0}
            </Badge>
          </div>

          <div className="mt-4 flex justify-between items-center">
            <div className="flex items-center space-x-1">
              <Badge className="bg-primary/20 border border-primary/30 text-primary">
                {
                  GameModeIcons[
                    room.spectatorMode as keyof typeof GameModeIcons
                  ]
                }
                <span className="ml-1 capitalize">{room.spectatorMode}</span>
              </Badge>
            </div>
          </div>
        </div>

        <CardContent className="p-5 bg-gradient-to-b from-black/40 to-black/60">
          <div className="grid grid-cols-2 gap-6 mb-2">
            <div className="space-y-1">
              <span className="text-xs text-white/50 uppercase">Host</span>
              <div className="font-medium text-sm truncate">
                {room.player1Info?.username}
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-xs text-white/50 uppercase">Opponent</span>
              <div className="font-medium text-sm truncate">
                {room.player2Info?.username || (
                  <span className="text-white/50 italic">Waiting...</span>
                )}
              </div>
            </div>
          </div>

          <div className="text-xs text-white/50 flex items-center mt-4">
            <Clock className="h-3 w-3 mr-1" />
            {new Date(room.createdAt).toLocaleString()}
          </div>
        </CardContent>

        <CardFooter className="p-4 bg-black/80 border-t border-white/5 flex justify-between items-center">
          <div className="text-xs text-white/60">ID: {room.id}</div>
          <Button
            onClick={() => handleWatchGame(room.id.toString())}
            className="bg-primary hover:bg-primary/90 text-black font-medium"
            size="sm"
          >
            <Tv className="h-4 w-4 mr-2" />
            Watch Game
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

function GameCardListView({
  room,
  gameStatus,
  handleWatchGame,
}: {
  room: any;
  gameStatus: "waiting" | "playing" | "full";
  handleWatchGame: (id: string) => void;
}) {
  return (
    <motion.div
      whileHover={{ x: 3, scale: 1.01 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
      <Card className="bg-black/60 backdrop-blur-md border border-white/10 hover:border-primary/60 transition-all">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            {/* Left side - Game info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-bold text-lg text-white">{room.name}</h3>
                <GameStatusIndicator status={gameStatus} />
                <Badge
                  variant="outline"
                  className="bg-black/60 border-white/20"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  {room.currentViewers || 0}
                </Badge>
              </div>

              <div className="flex items-center flex-wrap gap-2 mb-3">
                <Badge className="bg-primary/20 border border-primary/30 text-primary">
                  {
                    GameModeIcons[
                      room.spectatorMode as keyof typeof GameModeIcons
                    ]
                  }
                  <span className="ml-1 capitalize">{room.spectatorMode}</span>
                </Badge>

                <Badge
                  variant="outline"
                  className="text-xs bg-black/30 border-white/20"
                >
                  <Clock className="h-3 w-3 mr-1" />
                  {new Date(room.createdAt).toLocaleString()}
                </Badge>

                <Badge
                  variant="outline"
                  className="text-xs bg-black/30 border-white/20"
                >
                  ID: {room.id}
                </Badge>
              </div>
            </div>

            {/* Right side - Players and action */}
            <div className="flex items-center gap-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <span className="text-xs text-white/50 uppercase">Host</span>
                  <div className="font-medium text-sm">
                    {room.player1Info?.username}
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-white/50 uppercase">
                    Opponent
                  </span>
                  <div className="font-medium text-sm">
                    {room.player2Info?.username || (
                      <span className="text-white/50 italic">Waiting...</span>
                    )}
                  </div>
                </div>
              </div>

              <Button
                onClick={() => handleWatchGame(room.id.toString())}
                className="bg-primary hover:bg-primary/90 text-black font-medium whitespace-nowrap"
                size="sm"
              >
                <Tv className="h-4 w-4 mr-2" />
                Watch
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
